import React from 'react'
import { Link } from 'react-router-dom'
import { ClipboardCheck, FolderOpen, GitBranch, Mail, MapPin, Phone, type LucideIcon } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Spinner } from 'components/ui/spinner'
import useIsMobile from 'hooks/useIsMobile'
import routes from 'constants/routes'
import { mailtoHref, telHref } from 'utils/contact'
import { cn } from 'lib/utils'
import usePartnerShared, { type PartnerProfile, type SharedApproval, type SharedAsset } from '../hooks/usePartnerShared'

export interface PartnerProfileDialogProps {
	/** The partner's USER id. Null closes the dialog. */
	partnerUserId: string | null
	/**
	 * What the opening list already knows about this person. Shown at once,
	 * before the request lands, and kept when the person turns out not to be
	 * one of the viewer's partners (someone else's partner on a shared asset).
	 */
	fallback?: Partial<PartnerProfile> | null
	onClose: () => void
}

const DECISION_LABEL: Record<string, string> = {
	PENDING: 'Pending',
	APPROVED: 'Approved',
	REJECTED: 'Rejected',
	CHANGES_REQUESTED: 'Changes requested',
	DELEGATED: 'Delegated',
}

const chipClass = 'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium'

/** Whose it is, and readonly when it is. */
const OwnershipChips = ({ item }: { item: SharedAsset }) => (
	<>
		<span
			className={cn(chipClass, item.direction === 'mine' ? 'bg-highlight text-[#0080fe]' : 'bg-gray-100 text-gray-600')}
		>
			{item.direction === 'mine' ? 'Yours' : 'Theirs'}
		</span>
		{item.accessLevel === 'readonly' && <span className={cn(chipClass, 'bg-amber-50 text-amber-700')}>Read only</span>}
	</>
)

interface SectionProps {
	title: string
	icon: LucideIcon
	count: number
	empty: string
	children: React.ReactNode
}

const Section = ({ title, icon: Icon, count, empty, children }: SectionProps) => (
	<section aria-label={title}>
		<h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-1.5">
			<Icon className="w-4 h-4 text-gray-500" />
			{title}
			<span className="text-xs font-normal text-gray-400">{count}</span>
		</h3>
		{count === 0 ? (
			<p className="text-xs text-gray-400 pl-6 pb-1">{empty}</p>
		) : (
			<ul className="space-y-0.5">{children}</ul>
		)}
	</section>
)

const rowClass =
	'flex items-center gap-2 rounded-lg px-2 py-1.5 no-underline text-inherit hover:bg-gray-50 transition-colors'

/**
 * A partner's profile, and everything you share with them (MYD-533).
 *
 * The one popup every partner list opens: Home, the asset sidebars, Partners,
 * the Library and Approvals.
 *
 * Left column: who they are, with the email as a real `mailto:` link and, on a
 * phone or tablet, the number as a tap-to-call `tel:` link. Right column:
 * the locations, projects, portfolios and approvals the two of you share, in
 * either direction, each labelled Yours or Theirs.
 *
 * The phone is shown only on mobile, where tapping it calls. On a desktop a
 * `tel:` link usually opens nothing, and the number would read as an action
 * that does not work.
 */
const PartnerProfileDialog = ({ partnerUserId, fallback, onClose }: PartnerProfileDialogProps) => {
	const isMobile = useIsMobile()
	const state = usePartnerShared(partnerUserId)

	if (!partnerUserId) return null

	const data = state.status === 'ready' ? state.data : null
	const person: Partial<PartnerProfile> = { ...(fallback || {}), ...(data?.partner || {}) }
	const name = person.name || person.firstName || 'Partner'
	const subtitle = [person.title, person.company].filter(Boolean).join(', ')
	const mailto = mailtoHref(person.email)
	const tel = isMobile ? telHref(person.phone) : null

	const link = (to: string) => ({ to, onClick: onClose, className: rowClass })

	return (
		<Dialog open onClose={onClose}>
			<DialogContent className="max-w-3xl p-0">
				<div className="grid md:grid-cols-[240px_1fr]">
					{/* Who they are */}
					<div className="flex flex-col items-center text-center gap-2 p-6 bg-highlight md:border-r border-b md:border-b-0 border-gray-100">
						<Avatar className="h-24 w-24">
							<AvatarImage src={person.image} alt="" />
							<AvatarFallback className="text-2xl">{name.charAt(0).toUpperCase()}</AvatarFallback>
						</Avatar>
						<DialogTitle className="mt-1">{name}</DialogTitle>
						{subtitle && <p className="text-sm text-gray-500 m-0">{subtitle}</p>}
						{data && !data.isAccepted && (
							<span className={cn(chipClass, 'bg-amber-50 text-amber-700')}>Invitation pending</span>
						)}

						<div className="flex flex-col items-center gap-1.5 mt-2 w-full">
							{mailto ? (
								// One line, truncated, with the whole address on hover: a long
								// address broke mid-word across two lines in the 240px column.
								<a
									href={mailto}
									title={person.email}
									className="inline-flex items-center gap-1.5 max-w-full text-sm text-[#0080fe] hover:underline"
								>
									<Mail className="w-4 h-4 shrink-0" />
									<span className="truncate">{person.email}</span>
								</a>
							) : (
								person.email && (
									<span title={person.email} className="max-w-full truncate text-sm text-gray-500">
										{person.email}
									</span>
								)
							)}
							{tel && (
								<a href={tel} className="inline-flex items-center gap-1.5 text-sm text-[#0080fe] hover:underline">
									<Phone className="w-4 h-4 shrink-0" />
									{person.phone}
								</a>
							)}
						</div>
					</div>

					{/* What you share */}
					<div className="p-6 md:pr-12 md:max-h-[70vh] md:overflow-y-auto">
						{state.status === 'loading' || state.status === 'idle' ? (
							<Spinner containerHeight="12rem" />
						) : state.status === 'not-partner' ? (
							<p className="text-sm text-gray-500">
								{name} is not one of your partners, so there is nothing shared between you to show.
							</p>
						) : state.status === 'error' || !data ? (
							<p className="text-sm text-red-500">Could not load what you share. Close and try again.</p>
						) : (
							<div className="space-y-4">
								<Section title="Locations" icon={MapPin} count={data.locations.length} empty="No shared locations.">
									{data.locations.map(item => (
										<li key={item.id}>
											<Link {...link(`${routes.LOCATIONS}/${item.id}`)}>
												<span className="flex-1 min-w-0">
													<span className="block text-sm text-gray-900 truncate">{item.name}</span>
													{item.subtitle && (
														<span className="block text-xs text-gray-500 truncate">{item.subtitle}</span>
													)}
												</span>
												<OwnershipChips item={item} />
											</Link>
										</li>
									))}
								</Section>
								<Section title="Projects" icon={GitBranch} count={data.projects.length} empty="No shared projects.">
									{data.projects.map(item => (
										<li key={item.id}>
											<Link {...link(`${routes.PROJECTS}/${item.id}/tasks`)}>
												<span className="flex-1 min-w-0">
													<span className="block text-sm text-gray-900 truncate">{item.name}</span>
													{item.subtitle && (
														<span className="block text-xs text-gray-500 truncate">{item.subtitle}</span>
													)}
												</span>
												<OwnershipChips item={item} />
											</Link>
										</li>
									))}
								</Section>
								<Section
									title="Portfolios"
									icon={FolderOpen}
									count={data.portfolios.length}
									empty="No shared portfolios."
								>
									{data.portfolios.map(item => (
										<li key={item.id}>
											<Link {...link(`${routes.PORTFOLIOS}/${item.id}`)}>
												<span className="flex-1 min-w-0">
													<span className="block text-sm text-gray-900 truncate">{item.name}</span>
													{item.subtitle && (
														<span className="block text-xs text-gray-500 truncate">{item.subtitle}</span>
													)}
												</span>
												<OwnershipChips item={item} />
											</Link>
										</li>
									))}
								</Section>
								<Section
									title="Approvals"
									icon={ClipboardCheck}
									count={data.approvals.length}
									empty="No approvals between you."
								>
									{data.approvals.map((item: SharedApproval) => (
										<li key={item.id}>
											<Link {...link(`${routes.EVALUATIONS}/${item.evaluationId}`)}>
												<span className="flex-1 min-w-0">
													<span className="block text-sm text-gray-900 truncate">{item.name}</span>
													<span className="block text-xs text-gray-500">
														{item.direction === 'mine' ? `${name} approves` : 'You approve'}
													</span>
												</span>
												<span className={cn(chipClass, 'bg-gray-100 text-gray-600')}>
													{DECISION_LABEL[item.decision] || item.decision}
												</span>
											</Link>
										</li>
									))}
								</Section>
							</div>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default PartnerProfileDialog
